import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { obtenerPasajerosPorHash } from '../../api/giras.ts';
import { obtenerContactosEmergenciaPublico } from '../../api/contactoEmergencia.api.ts';

import { obtenerDocumentosCoordinador, descargarDocumentoCoordinador } from '../../api/documentosCoordinador.api.ts';
import './ListadoPasajerosPublicoPage.scss';

/**
 * Página pública para visualizar el listado de pasajeros de una gira
 * Requiere contraseña antes de acceder
 * @returns {JSX.Element} Página de listado de pasajeros
 */
function ListadoPasajerosPublicoPage() {
  const { hash } = useParams();
  const { t } = useTranslation();
  
  // Estados principales
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [giraData, setGiraData] = useState(null);
  const [pasajeros, setPasajeros] = useState([]);
  
  // Estados del formulario de contraseña
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados del modal de ficha
  const [showFichaModal, setShowFichaModal] = useState(false);
  const [selectedPasajero, setSelectedPasajero] = useState(null);
  const [contactosEmergencia, setContactosEmergencia] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);

  // Estados de filtros
  const [filtroAlimentacion, setFiltroAlimentacion] = useState(null);
  const [filtroCumpleaños, setFiltroCumpleaños] = useState(false);
  const [filtroPadecimiento, setFiltroPadecimiento] = useState(false);


    // Estados de documentos
    const [documentos, setDocumentos] = useState([]);
    const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  /**
   * Mapa de tipos de alimentación
   */
  const TIPOS_ALIMENTACION = {
    1: 'Sin Restricción',
    2: 'Vegetariano',
    3: 'Vegano',
    4: 'Intol. Lactosa',
    5: 'Celiaco'
  };

   /**
     * Carga los documentos de la gira cuando se abre el modal
     */
    useEffect(() => {
      
        const cargarDocs = async () => {
          setLoadingDocumentos(true);
          try {
            const resultado = await obtenerDocumentosCoordinador(giraData.gir_id);
            if (resultado.data) {
              setDocumentos(resultado.data);
            } else {
              setDocumentos([]);
            }
          } catch (error) {
            console.error('Error al cargar documentos:', error);
            setDocumentos([]);
          } finally {
            setLoadingDocumentos(false);
          }
        };
        
        cargarDocs();
      
    }, [giraData]);

  /**
   * Verifica si una fecha de nacimiento cae en el rango de la gira
   * @param {string} fechaNacimiento - Fecha de nacimiento (formato: YYYY-MM-DD)
   * @param {string} fechaInicio - Fecha inicio gira
   * @param {string} fechaFin - Fecha fin gira
   * @returns {boolean} True si hay cumpleaños en el rango
   */
  const estaEnRangoCumpleaños = (fechaNacimiento, fechaInicio, fechaFin) => {
    if (!fechaNacimiento) return false;

    // Obtener mes y día del cumpleaños
    const cumple = new Date(fechaNacimiento);
    const mesNacimiento = cumple.getMonth();
    const diaNacimiento = cumple.getDate();

    // Obtener rango de fechas
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);

    // Crear fechas de cumpleaños para este año y el próximo
    const cumplesteAño = new Date(inicio.getFullYear(), mesNacimiento, diaNacimiento);
    const cumplesteAñoProximo = new Date(fin.getFullYear(), mesNacimiento, diaNacimiento);

    // Verificar si cumpleaños cae en el rango
    return (cumplesteAño >= inicio && cumplesteAño <= fin) || 
           (cumplesteAñoProximo >= inicio && cumplesteAñoProximo <= fin);
  };

  /**
   * Obtiene cantidad de pasajeros de cumpleaños en el rango de gira
   */
  const obtenerCantidadCumpleaños = () => {
    if (!giraData) return 0;
    
    return pasajeros.filter(pasajero => 
      estaEnRangoCumpleaños(
        pasajero.alu_fnacimiento,
        giraData.gir_fviaje,
        giraData.gir_fterminoviaje
      )
    ).length;
  };

   /**
     * Descarga un documento de coordinador
     */
    const handleDescargarDocumento = async (documento) => {
       try {
              await descargarDocumentoCoordinador(documento.doc_id);
              } catch (error) {
                  console.error("Error al descargar:", error);
                  Swal.fire("Error", "Error al descargar el documento", "error");
              }
    };

  /**
   * Verifica si un pasajero tiene enfermedad o tratamiento registrado
   * Considera como "sin padecimiento" si el valor es: vacío, nulo, 'NO' o 'no'
   */
  const tienePadecimiento = (enfermedad, tratamiento) => {
    const esVacio = (valor) => {
      if (!valor) return true;
      const strValor = String(valor).trim().toUpperCase();
      return strValor === '' || strValor === 'NO' || strValor === 'NULL';
    };
    
    return !esVacio(enfermedad) || !esVacio(tratamiento);
  };

  /**
   * Obtiene resumen de cumpleaños
   */
  const obtenerResumenCumpleaños = () => {
    if (!giraData) return { nombre: 'Cumpleaños', cantidad: 0, color: '#ff6b6b' };
    
    const cantidad = pasajeros.filter(pasajero => 
      estaEnRangoCumpleaños(
        pasajero.alu_fnacimiento,
        giraData.gir_fviaje,
        giraData.gir_fterminoviaje
      )
    ).length;

    return { nombre: 'Cumpleaños', cantidad, color: '#ff6b6b' };
  };

  /**
   * Obtiene resumen de pasajeros con enfermedad/tratamiento
   */
  const obtenerResumenPadecimiento = () => {
    const cantidad = pasajeros.filter(pasajero => 
      tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento)
    ).length;

    return { nombre: 'Con Padecimiento', cantidad, color: '#e74c3c' };
  };

  /**
   * Obtiene pasajeros filtrados según los filtros aplicados
   */
  const obtenerPasajerosFiltrarados = () => {
    let pasajerosFiltrarados = [...pasajeros];

    // Filtro por tipo de alimentación
    if (filtroAlimentacion) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(
        p => Number(p.tal_id) === filtroAlimentacion
      );
    }

    // Filtro por cumpleaños
    if (filtroCumpleaños) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(p =>
        estaEnRangoCumpleaños(
          p.alu_fnacimiento,
          giraData.gir_fviaje,
          giraData.gir_fterminoviaje
        )
      );
    }

    // Filtro por padecimiento (enfermedad/tratamiento)
    if (filtroPadecimiento) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(p =>
        tienePadecimiento(p.alu_enfermedad, p.alu_tratamiento)
      );
    }

    return pasajerosFiltrarados;
  };

  /**
   * Calcula el resumen de tipos de alimentación
   */
  const obtenerResumenAlimentacion = () => {
    const resumen = {
      1: { nombre: 'Sin Restricción', cantidad: 0, color: '#28a745' },
      2: { nombre: 'Vegetariano', cantidad: 0, color: '#ffc107' },
      3: { nombre: 'Vegano', cantidad: 0, color: '#17a2b8' },
      4: { nombre: 'Intol. Lactosa', cantidad: 0, color: '#fd7e14' },
      5: { nombre: 'Celiaco', cantidad: 0, color: '#dc3545' }
    };

    pasajeros.forEach(pasajero => {
      const tipoAlimentacion = Number(pasajero.tal_id);
      console.log('Tipo de Alimentación del pasajero:', tipoAlimentacion);
      if (tipoAlimentacion && resumen[tipoAlimentacion]) {
        resumen[tipoAlimentacion].cantidad += 1;
      }
    });
    console.log('Resumen de Alimentación:', resumen);
    return Object.values(resumen);
  };

  /**
   * Valida la contraseña y obtiene el listado de pasajeros
   */
  const handleValidarPassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (!password.trim()) {
      setPasswordError('Ingrese la contraseña');
      return;
    }

    setLoading(true);
    try {
      const data = await obtenerPasajerosPorHash(hash, password);

      if (data.error || !data.success) {
        if (data.status === 401) {
          setPasswordError('Contraseña incorrecta');
        } else {
          setError(data.message || 'Error al conectar con el servidor');
        }
      } else {
        setGiraData(data.gira);
        setPasajeros(data.pasajeros || []);
        setAuthenticated(true);
        setPassword('');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abre el modal con detalles del pasajero y carga sus contactos de emergencia
   */
  const handleVerFicha = async (pasajero) => {
    setSelectedPasajero(pasajero);
    setShowFichaModal(true);
    
    // Cargar contactos de emergencia del pasajero
    setLoadingContactos(true);
    try {
      const resultado = await obtenerContactosEmergenciaPublico(pasajero.alu_id);
      if ( resultado.data) {
        setContactosEmergencia(resultado.data);
      } else {
        setContactosEmergencia([]);
      }
    } catch (error) {
      console.error('Error al cargar contactos:', error);
      setContactosEmergencia([]);
    } finally {
      setLoadingContactos(false);
    }
  };

  /**
   * Cierra el modal de ficha
   */
  const handleCerrarFicha = () => {
    setShowFichaModal(false);
    setSelectedPasajero(null);
  };


   /**
   * Obtiene resumen de pasajeros por género (Alumnos y Adultos)
   * Agrupa:
   * - Alumnos: Alumno(a), Alumno(a) 100% Liberado, Alumno(a) 50% Liberado
   * - Adultos: Adulto, Adulto Liberado 100%, Adulto Liberado 50%
   */
  const obtenerResumenPasajerosPorGenero = () => {
    const resumen = {
      alumnos: { femenino: 0, masculino: 0 },
      adultos: { femenino: 0, masculino: 0 }
    };

    pasajeros.forEach(pasajero => {
      const tipoPasajero = (pasajero.tipo_pasajero || '').toLowerCase().trim();
      const genero = (pasajero.genero || '').toLowerCase().trim();

      // Determinar si es alumno o adulto
      const esAlumno = tipoPasajero.includes('alumno');
      const esAdulto = tipoPasajero.includes('adulto');

      // Contar por género
      if (genero === 'femenino' || genero === 'f') {
        if (esAlumno) resumen.alumnos.femenino++;
        if (esAdulto) resumen.adultos.femenino++;
      } else if (genero === 'masculino' || genero === 'm') {
        if (esAlumno) resumen.alumnos.masculino++;
        if (esAdulto) resumen.adultos.masculino++;
      }
    });

    return resumen;
  };

  /**
   * Pantalla de ingreso de contraseña
   */
  if (!authenticated) {
    return (
      <div className="password-screen">
        <Container fluid className="h-100">
          <Row className="h-100 align-items-center">
            <Col xs={12} sm={10} md={8} lg={6} className="mx-auto">
              <Card className="password-card shadow-lg">
                <Card.Body className="p-4 p-sm-5">
                  <div className="text-center mb-4">
                    <i className="material-icons password-icon">lock</i>
                    <h1 className="mt-3 mb-2">{'Acceso Restringido'}</h1>
                    <p className="text-muted">{'Ingrese la contraseña para continuar'}</p>
                  </div>

                  {error && <Alert variant="danger">{error}</Alert>}

                  <Form onSubmit={handleValidarPassword}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        { 'Contraseña'}
                      </Form.Label>
                      <div className="input-group password-input-group">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'Ingrese contraseña'}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError(null);
                          }}
                          isInvalid={!!passwordError}
                          className="password-field"
                          disabled={loading}
                          autoFocus
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          <i className="material-icons">
                            {showPassword ? 'visibility_off' : 'visibility'}
                          </i>
                        </Button>
                      </div>
                      {passwordError && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {passwordError}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" animation="border" />
                          Validando...
                        </>
                      ) : (
                        <>
                          <i className="material-icons me-2">check</i>
                          { 'Continuar'}
                        </>
                      )}
                    </Button>
                  </Form>

                  <hr className="my-4" />

                  <div className="text-center">
                    <small className="text-muted">
                      <i className="material-icons" style={{ fontSize: '0.9rem' }}>
                        info
                      </i>
                      { 'Acceso mediante código QR'}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  /**
   * Pantalla de listado de pasajeros
   */
  return (
    <Container fluid className="pasajeros-publico py-4 py-sm-5">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="header-titulo">
            <h1 className="text-primary mb-1">
              <i className="material-icons">list_alt</i>
              {'Listado de Pasajeros'}
            </h1>
            <p className="text-muted mb-0">{giraData?.est_nombre || 'Establecimiento'}</p>
          </div>
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setAuthenticated(false);
              setPasajeros([]);
              setGiraData(null);
            }}
          >
            <i className="material-icons">exit_to_app</i>
            Salir
          </Button>
        </Col>
      </Row>

      {/* Información de la Gira */}
      {giraData && (
        <Row className="mb-4">
          <Col xs={12} lg={10}>
            <Card className="gira-info-card shadow-sm">
              {/* Detalles de la Gira */}
              <Card.Body className="p-3 p-sm-4">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>info_outline</i>
                  Detalles de la Gira
                </h6>
                <Row className="g-3 mb-4">
                  {giraData.gir_ano && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Año</small>
                        <p className="mb-0">{giraData.gir_ano}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_programa && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Programa</small>
                        <p className="mb-0">{giraData.gir_programa}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_tramo && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Tramo</small>
                        <p className="mb-0">{giraData.gir_tramo}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_region && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Región</small>
                        <p className="mb-0">{giraData.gir_region}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_comuna && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Comuna</small>
                        <p className="mb-0">{giraData.gir_comuna}</p>
                      </div>
                    </Col>
                  )}
                </Row>

                <Row className="g-3 mb-4">
                  {giraData.est_nombre && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Establecimiento</small>
                        <p className="mb-0">{giraData.est_nombre}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_curso && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Curso</small>
                        <p className="mb-0">{giraData.gir_curso}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_liberados && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Liberados</small>
                        <p className="mb-0">{giraData.gir_liberados}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_fviaje && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Fecha Viaje</small>
                        <p className="mb-0">{new Date(giraData.gir_fviaje).toLocaleDateString('es-CL')}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_fterminoviaje && (
                    <Col xs={12} sm={6} md={2.4}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Fecha Término</small>
                        <p className="mb-0">{new Date(giraData.gir_fterminoviaje).toLocaleDateString('es-CL')}</p>
                      </div>
                    </Col>
                  )}
                </Row>

                {(giraData.gir_resp_curso_nombre || giraData.gir_resp_curso_celular || giraData.gir_resp_curso_email) && (
                  <Row className="g-3 mb-4">
                    {giraData.gir_resp_curso_nombre && (
                      <Col xs={12} sm={6} md={4}>
                        <div className="info-item">
                          <small className="text-muted d-block fw-bold">Responsable del Curso</small>
                          <p className="mb-0">{giraData.gir_resp_curso_nombre}</p>
                        </div>
                      </Col>
                    )}
                    {giraData.gir_resp_curso_celular && (
                      <Col xs={12} sm={6} md={4}>
                        <div className="info-item">
                          <small className="text-muted d-block fw-bold">Celular</small>
                          <p className="mb-0">
                            <a href={`tel:${giraData.gir_resp_curso_celular}`} className="link-primary">
                              {giraData.gir_resp_curso_celular}
                            </a>
                          </p>
                        </div>
                      </Col>
                    )}
                    {giraData.gir_resp_curso_email && (
                      <Col xs={12} sm={6} md={4}>
                        <div className="info-item">
                          <small className="text-muted d-block fw-bold">Email</small>
                          <p className="mb-0">
                            <a href={`mailto:${giraData.gir_resp_curso_email}`} className="link-primary">
                              {giraData.gir_resp_curso_email}
                            </a>
                          </p>
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
  </Card.Body>
                <hr className="my-3" />

                {/* Información Básica */}
              <Card.Body className="p-3 p-sm-4">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>info</i>
                  Información Básica
                </h6>
                <Row className="g-3 mb-4">
                  <Col xs={12} sm={6} md={2.4}>
                    <div className="info-item">
                      <small className="text-muted d-block fw-bold">Curso</small>
                      <p className="mb-0 fs-5">{giraData.gir_curso}</p>
                    </div>
                  </Col>
                  <Col xs={12} sm={6} md={2.4}>
                    <div className="info-item">
                      <small className="text-muted d-block fw-bold">Inicio</small>
                      <p className="mb-0 fs-5">
                        {new Date(giraData.gir_fviaje).toLocaleDateString('es-CL')}
                      </p>
                    </div>
                  </Col>
                  <Col xs={12} sm={6} md={2.4}>
                    <div className="info-item">
                      <small className="text-muted d-block fw-bold">Término</small>
                      <p className="mb-0 fs-5">
                        {new Date(giraData.gir_fterminoviaje).toLocaleDateString('es-CL')}
                      </p>
                    </div>
                  </Col>
                  <Col xs={12} sm={6} md={2.4}>
                    <div className="info-item">
                      <small className="text-muted d-block fw-bold">Total Pasajeros</small>
                      <p className="mb-0 fs-5">
                        <span className="badge bg-primary">{giraData.total_pasajeros}</span>
                      </p>
                    </div>
                  </Col>
                </Row>

                <hr className="my-3" />

                {/* Información de Transporte */}
                <h6 className="fw-bold text-primary mb-3">
                  <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>flight_takeoff</i>
                  Información de Transporte
                </h6>
                <Row className="g-3 mb-4">
                  {giraData.gir_tipo_transporte && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Tipo de Transporte</small>
                        <p className="mb-0">{giraData.gir_tipo_transporte}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_aerolinea && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Aerolínea</small>
                        <p className="mb-0">{giraData.gir_aerolinea}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_num_vuelo && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Número de Vuelo</small>
                        <p className="mb-0">{giraData.gir_num_vuelo}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_horario_vuelo_ida && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Horario Vuelo (Ida)</small>
                        <p className="mb-0">{giraData.gir_horario_vuelo_ida}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_horario_vuelo_regreso && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Horario Vuelo (Regreso)</small>
                        <p className="mb-0">{giraData.gir_horario_vuelo_regreso}</p>
                      </div>
                    </Col>
                  )}
                  {giraData.gir_patente_bus && (
                    <Col xs={12} sm={6} md={3}>
                      <div className="info-item">
                        <small className="text-muted d-block fw-bold">Patente Bus</small>
                        <p className="mb-0">{giraData.gir_patente_bus}</p>
                      </div>
                    </Col>
                  )}
                </Row>

                {/* Información de Choferes */}
                {(giraData.gir_nombre_chofer1 || giraData.gir_nombre_chofer2) && (
                  <>
                    <hr className="my-3" />
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>person_outline</i>
                      Información de Choferes
                    </h6>
                    <Row className="g-3 mb-4">
                      {giraData.gir_nombre_chofer1 && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Chofer 1</small>
                            <p className="mb-0">{giraData.gir_nombre_chofer1}</p>
                          </div>
                        </Col>
                      )}
                      {giraData.gir_nombre_chofer2 && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Chofer 2</small>
                            <p className="mb-0">{giraData.gir_nombre_chofer2}</p>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </>
                )}

                {/* Información de Horarios */}
                {(giraData.gir_horario_salida || giraData.gir_horario_regreso || giraData.gir_horario_encuentro) && (
                  <>
                    <hr className="my-3" />
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>schedule</i>
                      Horarios
                    </h6>
                    <Row className="g-3 mb-4">
                      {giraData.gir_horario_encuentro && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Horario Encuentro</small>
                            <p className="mb-0">{giraData.gir_horario_encuentro}</p>
                          </div>
                        </Col>
                      )}
                      {giraData.gir_horario_salida && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Horario Salida</small>
                            <p className="mb-0">{giraData.gir_horario_salida}</p>
                          </div>
                        </Col>
                      )}
                      {giraData.gir_horario_regreso && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Horario Regreso</small>
                            <p className="mb-0">{giraData.gir_horario_regreso}</p>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </>
                )}

                {/* Información Adicional */}
                {(giraData.gir_hotel || giraData.gir_punto_encuentro || giraData.gir_coordinador) && (
                  <>
                    <hr className="my-3" />
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>business</i>
                      Información Adicional
                    </h6>
                    <Row className="g-3">
                      {giraData.gir_hotel && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Hotel</small>
                            <p className="mb-0">{giraData.gir_hotel}</p>
                          </div>
                        </Col>
                      )}
                      {giraData.gir_punto_encuentro && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Punto Encuentro</small>
                            <p className="mb-0">{giraData.gir_punto_encuentro}</p>
                          </div>
                        </Col>
                      )}
                      {giraData.gir_coordinador && (
                        <Col xs={12} sm={6} md={3}>
                          <div className="info-item">
                            <small className="text-muted d-block fw-bold">Coordinador</small>
                            <p className="mb-0">{giraData.gir_coordinador}</p>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
  {/* Resumen de Pasajeros por Género */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card className="genero-resumen-card shadow-sm">
                    <Card.Header className="bg-gradient">
                      <Card.Title className="mb-0">
                        <i className="material-icons me-2">wc</i>
                        Resumen de Pasajeros por Género
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {(() => {
                        const resumen = obtenerResumenPasajerosPorGenero();
                        return (
                          <Row className="g-3">
                            {/* Alumnos */}
                            <Col xs={12} sm={6} md={3}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>school</i>
                                  Alumnos
                                </h6>
                                <div className="gender-item mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Femenino</span>
                                    <span className="badge bg-danger">{resumen.alumnos.femenino}</span>
                                  </div>
                                </div>
                                <div className="gender-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Masculino</span>
                                    <span className="badge bg-primary">{resumen.alumnos.masculino}</span>
                                  </div>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between align-items-center fw-bold">
                                  <span>Total</span>
                                  <span className="badge bg-info">{resumen.alumnos.femenino + resumen.alumnos.masculino}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Adultos */}
                            <Col xs={12} sm={6} md={3}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>person</i>
                                  Adultos
                                </h6>
                                <div className="gender-item mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Femenino</span>
                                    <span className="badge bg-danger">{resumen.adultos.femenino}</span>
                                  </div>
                                </div>
                                <div className="gender-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Masculino</span>
                                    <span className="badge bg-primary">{resumen.adultos.masculino}</span>
                                  </div>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between align-items-center fw-bold">
                                  <span>Total</span>
                                  <span className="badge bg-info">{resumen.adultos.femenino + resumen.adultos.masculino}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Totales Generales */}
                            <Col xs={12} sm={12} md={6}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>summarize</i>
                                  Totales Generales
                                </h6>
                                <Row className="g-2">
                                  <Col xs={6}>
                                    <div className="total-item text-center p-2 bg-light rounded">
                                      <small className="text-muted d-block fw-bold">Total Femenino</small>
                                      <p className="mb-0 fs-5">
                                        <span className="badge bg-danger">{resumen.alumnos.femenino + resumen.adultos.femenino}</span>
                                      </p>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="total-item text-center p-2 bg-light rounded">
                                      <small className="text-muted d-block fw-bold">Total Masculino</small>
                                      <p className="mb-0 fs-5">
                                        <span className="badge bg-primary">{resumen.alumnos.masculino + resumen.adultos.masculino}</span>
                                      </p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          </Row>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

 {/* Sección de Documentos de Coordinador */}
          {giraData && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="documentos-coordinador-card shadow-sm">
                  <Card.Header className="bg-gradient">
                    <Card.Title className="mb-0">
                      <i className="material-icons me-2">description</i>
                      Documentos de la Gira
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {loadingDocumentos ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="mb-2" />
                        <p className="text-muted small">Cargando documentos...</p>
                      </div>
                    ) : documentos && documentos.length > 0 ? (
                      <div className="documentos-grid">
                        <div className="table-responsive">
                          <Table striped hover size="sm" className="mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th>Nombre</th>
                                <th>Tipo</th>
                                <th>Descripción</th>
                                <th className="text-center">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentos.map((doc, index) => (
                                <tr key={doc.doc_id || index}>
                                  <td className="fw-bold small">
                                    <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>
                                      {doc.doc_tipo === 'PDF' ? 'picture_as_pdf' : 
                                       doc.doc_tipo === 'Excel' ? 'description' : 
                                       'insert_drive_file'}
                                    </i>
                                    {doc.doc_nombre}
                                  </td>
                                  <td>
                                    <span className="badge bg-info">{doc.doc_tipo || 'Documento'}</span>
                                  </td>
                                  <td className="small text-muted">{doc.doc_descripcion || '-'}</td>
                                  <td className="text-center">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleDescargarDocumento(doc)}
                                      title="Descargar documento"
                                    >
                                      <i className="material-icons" style={{ fontSize: '0.9rem' }}>download</i>
                                      Descargar
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>info</i>
                        No hay documentos disponibles para esta gira
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
      {/* Resumen de Tipos de Alimentación */}
      {pasajeros && pasajeros.length > 0 && (
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="alimentacion-resumen-card shadow-sm">
              <Card.Header className="bg-light">
                <Card.Title className="mb-0">
                  <i className="material-icons">restaurant</i>
                  Resumen de Tipos de Alimentación, Cumpleaños y Diagnósticos Médicos
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3 p-sm-4">
                <Row className="g-3">
                  {/* Resumen de Cumpleaños */}
                  <Col xs={12} sm={6} md={4} lg={2.4} key="cumpleaños" className="mb-2">
                    <div 
                      className={`alimentacion-item text-center ${filtroCumpleaños ? 'active-filter' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setFiltroCumpleaños(!filtroCumpleaños);
                        setFiltroAlimentacion(null);
                        setFiltroPadecimiento(false);
                      }}
                    >
                      <div 
                        className="alimentacion-badge mb-2"
                        style={{ backgroundColor: obtenerResumenCumpleaños().color }}
                      >
                        {obtenerResumenCumpleaños().cantidad}
                      </div>
                      <small className="text-muted d-block fw-bold">Cumpleaños</small>
                    </div>
                  </Col>

                  {/* Resumen de Padecimiento */}
                  <Col xs={12} sm={6} md={4} lg={2.4} key="padecimiento" className="mb-2">
                    <div 
                      className={`alimentacion-item text-center ${filtroPadecimiento ? 'active-filter' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setFiltroPadecimiento(!filtroPadecimiento);
                        setFiltroAlimentacion(null);
                        setFiltroCumpleaños(false);
                      }}
                    >
                      <div 
                        className="alimentacion-badge mb-2"
                        style={{ backgroundColor: obtenerResumenPadecimiento().color }}
                      >
                        {obtenerResumenPadecimiento().cantidad}
                      </div>
                      <small className="text-muted d-block fw-bold">Con Diagnósticos Médicos</small>
                    </div>
                  </Col>

                  {/* Resumen de Alimentación */}
                  {obtenerResumenAlimentacion().map((item) => {
                    const tipoMap = { 'Sin Restricción': 1, 'Vegetariano': 2, 'Vegano': 3, 'Intol. Lactosa': 4, 'Celiaco': 5 };
                    const tipoId = tipoMap[item.nombre];
                    return (
                      <Col xs={12} sm={6} md={4} lg={2.4} key={item.nombre} className="mb-2">
                        <div 
                          className={`alimentacion-item text-center ${filtroAlimentacion === tipoId ? 'active-filter' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setFiltroAlimentacion(filtroAlimentacion === tipoId ? null : tipoId);
                            setFiltroCumpleaños(false);
                            setFiltroPadecimiento(false);
                          }}
                        >
                          <div 
                            className="alimentacion-badge mb-2"
                            style={{ backgroundColor: item.color }}
                          >
                            {item.cantidad}
                          </div>
                          <small className="text-muted d-block fw-bold">{item.nombre}</small>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                {(filtroAlimentacion || filtroCumpleaños || filtroPadecimiento) && (
                  <div className="mt-3 text-center">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => {
                        setFiltroAlimentacion(null);
                        setFiltroCumpleaños(false);
                        setFiltroPadecimiento(false);
                      }}
                    >
                      <i className="material-icons" style={{ fontSize: '0.9rem' }}>close</i>
                      Limpiar Filtros
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabla de Pasajeros - Desktop */}
      <Row className="mb-4 d-none d-lg-block">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <i className="material-icons">people</i>
                Pasajeros ({giraData?.total_pasajeros || 0})
              </Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              {pasajeros && pasajeros.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>#</th>
                        <th>Nombre Completo</th>
                        <th>RUT</th>
                         <th>Tipo Pasajero</th>
                           <th>Género</th>
                        <th className="text-center">Cumpleaños</th>
                        <th className="text-center">Diagnósticos Médicos</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {obtenerPasajerosFiltrarados().map((pasajero, index) => {
                        const tieneCumpleaños = estaEnRangoCumpleaños(
                          pasajero.alu_fnacimiento,
                          giraData.gir_fviaje,
                          giraData.gir_fterminoviaje
                        );
                        const tienePadc = tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento);
                        const rowClass = tieneCumpleaños ? 'table-warning' : tienePadc ? 'table-info' : '';
                        return (
                          <tr key={pasajero.alu_id} className={rowClass}>
                            <td className="fw-bold">{index + 1}</td>
                            <td>{`${pasajero.alu_nombres} ${pasajero.alu_apellidos}`} {pasajero.alu_nombre_social && (<span className="badge bg-danger">Nombre Social: {pasajero.alu_nombre_social}</span>)}</td>
                            <td>{pasajero.alu_rut}-{pasajero.alu_dv}</td>
                            <td>{pasajero.tipo_pasajero}</td>
                            <td>{pasajero.genero}</td>
                            <td className="text-center">
                              {tieneCumpleaños && (
                                <span className="badge bg-danger">
                                  <i className="material-icons" style={{ fontSize: '0.85rem' }}>cake</i>
                                  {' '}
                                  {new Date(pasajero.alu_fnacimiento).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              {tienePadc && (
                                <span className="badge bg-warning text-dark">
                                  <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                                  {' '}Diagnósticos Médicos
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleVerFicha(pasajero)}
                              >
                                <i className="material-icons">visibility</i>
                                Ver
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No hay pasajeros registrados en esta gira
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tarjetas de Pasajeros - Mobile/Tablet */}
      <Row className="d-lg-none">
        {obtenerPasajerosFiltrarados() && obtenerPasajerosFiltrarados().length > 0 ? (
          obtenerPasajerosFiltrarados().map((pasajero, index) => {
            const tieneCumpleaños = estaEnRangoCumpleaños(
              pasajero.alu_fnacimiento,
              giraData.gir_fviaje,
              giraData.gir_fterminoviaje
            );
            const tienePadc = tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento);
            const borderClass = tieneCumpleaños ? 'border-warning border-2' : tienePadc ? 'border-info border-2' : '';
            return (
              <Col xs={12} sm={6} md={4} key={pasajero.alu_id} className="mb-3">
                <Card className={`pasajero-card h-100 shadow-sm ${borderClass}`}>
                  <Card.Body>
                    <div className="pasajero-number">#{index + 1}</div>
                    {tieneCumpleaños && (
                      <div className="cumpleaños-badge mb-2">
                        <i className="material-icons">cake</i>
                        {new Date(pasajero.alu_fnacimiento).toLocaleDateString('es-CL', { month: 'short', day: 'numeric' })}
                      </div>
                    )}
                   
                    <div className="mb-2">
                        <span className="badge bg-warning text-dark">
                          <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                          {' '}{pasajero.tipo_pasajero}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="badge bg-warning text-dark">
                          <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                          {' '}{pasajero.genero}
                        </span>
                      </div>        
                    {tienePadc && (
                      <div className="mb-2">
                        <span className="badge bg-warning text-dark">
                          <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                          {' '}Diagnóstico Médico
                        </span>
                      </div>
                    )}
                    <h5 className="card-title mb-2">{`${pasajero.alu_nombres} ${pasajero.alu_apellidos}`}</h5>
                    {pasajero.alu_nombre_social && (
                      <p className="mb-2">
                        <span className="badge bg-danger">Nombre Social: {pasajero.alu_nombre_social}</span>
                      </p>
                    )}

                    <div className="pasajero-info mb-3">
                      <div className="info-row">
                        <small className="text-muted">RUT</small>
                        <p className="mb-0">{pasajero.alu_rut}-{pasajero.alu_dv}</p>
                      </div>
                     
                    </div>

                    <Button
                      variant="primary"
                      size="sm"
                      className="w-100"
                    onClick={() => handleVerFicha(pasajero)}
                  >
                    <i className="material-icons">visibility</i>
                    Ver Ficha
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            );
          })
        ) : (
          <Col xs={12}>
            <Alert variant="info">
              No hay pasajeros registrados en esta gira
            </Alert>
          </Col>
        )}
      </Row>

      {/* Modal de Ficha del Pasajero */}
      <Modal show={showFichaModal} onHide={handleCerrarFicha} centered className="ficha-modal" size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="material-icons">person</i>
            Ficha del Pasajero
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedPasajero && (
            <div className="ficha-contenido">
              {/* Header con nombre */}
              <div className="ficha-header text-center bg-light p-4 border-bottom">
                <div className="avatar-ficha mb-3">
                  <i className="material-icons">person_outline</i>
                </div>
                <h4 className="mb-1 fw-bold">
                  {selectedPasajero.alu_nombres} {selectedPasajero.alu_apellidos}
                </h4>
                {selectedPasajero.alu_nombre_social && (
                  <p className="mb-2">
                    <span className="badge bg-danger">Nombre Social: {selectedPasajero.alu_nombre_social}</span>
                  </p>
                )}
                <small className="text-muted">RUT: {selectedPasajero.alu_rut}-{selectedPasajero.alu_dv}</small>
              </div>

              {/* Contenido con tabs/secciones */}
              <div className="ficha-body p-4">
                {/* Sección: Datos del Pasajero */}
                <div className="ficha-section mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>person</i>
                    Datos del Pasajero
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">RUT</small>
                        <p className="mb-0">{selectedPasajero.alu_rut}-{selectedPasajero.alu_dv}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tipo de Pasajero</small>
                        <p className="mb-0">{selectedPasajero.tipo_pasajero || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Nacionalidad</small>
                        <p className="mb-0">{selectedPasajero.alu_nacionalidad || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Género</small>
                        <p className="mb-0">{selectedPasajero.genero || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Fecha de Nacimiento</small>
                        <p className="mb-0">
                          {selectedPasajero.alu_fnacimiento 
                            ? new Date(selectedPasajero.alu_fnacimiento).toLocaleDateString('es-CL')
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Email</small>
                        <p className="mb-0">
                          {selectedPasajero.alu_correo ? (
                            <a href={`mailto:${selectedPasajero.alu_correo}`} className="link-primary">
                              {selectedPasajero.alu_correo}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                {/* Sección: Ficha Médica */}
                <div className="ficha-section mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>medical_services</i>
                    Ficha Médica
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tipo Alimentación</small>
                        <p className="mb-0">{selectedPasajero.tipo_alimentacion || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Grupo Sanguíneo</small>
                        <p className="mb-0">{selectedPasajero.grupo_sanguineo || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Talla Polera</small>
                        <p className="mb-0">{selectedPasajero.talla_polera || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">N° Voucher Seguro Médico</small>
                        <p className="mb-0">{selectedPasajero.ts_voucher_seguro || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Alergias</small>
                        <p className="mb-0">{selectedPasajero.alu_alergia || 'No registra'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Enfermedad</small>
                        <p className="mb-0">{selectedPasajero.alu_enfermedad || 'No registra'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-0">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tratamiento</small>
                        <p className="mb-0">{selectedPasajero.alu_tratamiento || 'No registra'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                {/* Sección: Contacto Adulto Responsable */}
                {selectedPasajero.apo_nombres && (
                  <div className="ficha-section mb-4">
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>contacts</i>
                      Contacto Adulto Responsable
                    </h6>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">RUT</small>
                          <p className="mb-0">{selectedPasajero.apo_rut}-{selectedPasajero.apo_dv}</p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Parentesco</small>
                          <p className="mb-0">{selectedPasajero.parentesco || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="col-12 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Nombres y Apellidos</small>
                          <p className="mb-0">{selectedPasajero.apo_nombres} {selectedPasajero.apo_apellidos}</p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Email</small>
                          <p className="mb-0">
                            {selectedPasajero.apo_email ? (
                              <a href={`mailto:${selectedPasajero.apo_email}`} className="link-primary">
                                {selectedPasajero.apo_email}
                              </a>
                            ) : (
                              'N/A'
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-0">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Teléfono Celular</small>
                          <p className="mb-0">{selectedPasajero.apo_celular || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <hr />

                {/* Sección: Contacto de Emergencia */}
                <div className="ficha-section">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>phone_in_talk</i>
                    Contacto de Emergencia
                  </h6>
                  {loadingContactos ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" className="mb-2" />
                      <p className="text-muted small">Cargando contactos...</p>
                    </div>
                  ) : contactosEmergencia && contactosEmergencia.length > 0 ? (
                    <div className="contactos-list">
                      {contactosEmergencia.map((contacto, idx) => (
                        <div key={contacto.cem_id || idx} className="contacto-item mb-4 pb-3 border-bottom">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">RUT</small>
                                <p className="mb-0">{contacto.cem_rut}-{contacto.cem_dv}</p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Parentesco</small>
                                <p className="mb-0">{contacto.cem_parentesco || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="col-12 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Nombres y Apellidos</small>
                                <p className="mb-0">{contacto.cem_nombres} {contacto.cem_apellidos}</p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Email</small>
                                <p className="mb-0">
                                  {contacto.cem_email ? (
                                    <a href={`mailto:${contacto.cem_email}`} className="link-primary">
                                      {contacto.cem_email}
                                    </a>
                                  ) : (
                                    'N/A'
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-0">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Teléfono Celular</small>
                                <p className="mb-0">{contacto.cem_celular || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>info</i>
                      No hay contactos de emergencia registrados
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarFicha}>
            Cerrar
          </Button>
          {selectedPasajero?.alu_correo && (
            <Button
              variant="primary"
              href={`mailto:${selectedPasajero.alu_correo}`}
            >
              <i className="material-icons">mail</i>
              Enviar Email al Alumno
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ListadoPasajerosPublicoPage;

